import { Button, InputOTP, InputOTPGroup, InputOTPSlot } from 'components'
import { cn } from 'lib/utils'

interface OtpVerificationFormViewProps {
  email: string
  code: string
  setCode: (code: string) => void
  isVerifying: boolean
  isRequesting: boolean
  error: string | undefined
  resendCooldown: number
  onSubmit: () => void
  onResendCode: () => void
  onBack: () => void
  /** Optional custom className for the submit button (e.g., for social flow styling) */
  buttonClassName?: string
}

/**
 * OTP verification view on the Taka design system.
 *
 * Active slot lights up acid as the live-state cue. Resend countdown is
 * mono so it reads as metadata, not as a button.
 */
export const OtpVerificationFormView: React.FC<OtpVerificationFormViewProps> = ({
  email,
  code,
  setCode,
  isVerifying,
  isRequesting,
  error,
  resendCooldown,
  onSubmit,
  onResendCode,
  onBack,
  buttonClassName,
}) => {
  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-2">
        <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
          Step 2 of 2
        </span>
        <h2 className="text-[28px] leading-[1.1] font-semibold tracking-[-0.02em] text-paper">
          Check your inbox.
        </h2>
        <p className="lede !text-[15px] !leading-[1.55]">
          We sent a 6-digit code to <strong className="font-semibold text-paper">{email}</strong>.
        </p>
        <button
          type="button"
          onClick={onBack}
          disabled={isVerifying}
          className="mono mt-1 self-start text-[11px] tracking-[0.05em] text-paper-dim underline underline-offset-2 hover:text-paper disabled:opacity-50"
        >
          Not you? Use a different email
        </button>
      </div>

      <form
        onSubmit={e => {
          e.preventDefault()
          onSubmit()
        }}
        className="flex flex-col gap-5"
      >
        <div className="flex justify-start">
          <InputOTP
            autoFocus
            id="code"
            value={code}
            onChange={setCode}
            onComplete={onSubmit}
            maxLength={6}
            disabled={isVerifying}
            aria-invalid={!!error}
            render={({ slots }) => (
              <InputOTPGroup className="gap-2 sm:gap-2.5">
                {slots.map((slot, index) => (
                  <InputOTPSlot
                    key={index}
                    {...slot}
                    className={cn(
                      'h-12 w-12 rounded-[9px] border border-line-bright bg-bg-3 text-[18px] font-semibold text-paper transition-all',
                      slot.isActive && 'border-acid shadow-[0_0_0_3px_rgba(198,255,61,0.18)]',
                      error && 'border-red-400/60'
                    )}
                  />
                ))}
              </InputOTPGroup>
            )}
          />
        </div>

        <Button
          type="submit"
          variant="t3-primary"
          size="t3-lg"
          disabled={isVerifying || code.length !== 6}
          data-testid="otp-submit"
          className={buttonClassName}
        >
          {isVerifying ? 'Verifying…' : 'Verify and sign in'}
        </Button>

        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
      </form>

      <div className="flex items-center justify-between gap-3 border-t border-line pt-5 text-[13px] text-paper-dim">
        <span>Didn't get it?</span>
        <button
          type="button"
          onClick={onResendCode}
          disabled={resendCooldown > 0 || isRequesting}
          className="mono text-[12px] tracking-[0.04em] text-paper hover:text-acid disabled:cursor-not-allowed disabled:text-paper-faint disabled:hover:text-paper-faint"
        >
          {resendCooldown > 0
            ? `Resend in ${String(Math.floor(resendCooldown / 60)).padStart(2, '0')}:${String(resendCooldown % 60).padStart(2, '0')}`
            : isRequesting
              ? 'Sending…'
              : 'Resend code'}
        </button>
      </div>
    </div>
  )
}
